import type { Theme } from '@mui/material';
import { Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useUnitProvider } from '/@/core/context/unit';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { formatDate } from '/@/utils';
import { getHarvestHybridNames, getSharedHarvestHybrids } from '/@/utils/crop-monitoring';
import { getCrops } from '/@/views/dashboard/hooks/crops';

const ReportHeaderData = ({
    cropMonitoring,
    report,
    field,
    plantingYear,
    harvestYear,
    unitsProp,
}: {
    cropMonitoring: CropMonitoring;
    report: CropMonitoring['result'];
    field: Field;
    plantingYear: number;
    harvestYear: number;
    unitsProp: any;
}) => {
    const units = useUnitProvider(unitsProp);
    const fieldArea = convertAreaFromM2(field?.area || 0, units).toString();
    const crops = getCrops();

    const data = useMemo(() => {
        // A harvest shared by several hybrids lists them, with their planting data, in its own
        // card: here only what the harvest has in common.
        const shared = getSharedHarvestHybrids(cropMonitoring).length > 0;
        const hybridNames = cropMonitoring ? getHarvestHybridNames(cropMonitoring) : [];
        return [
            { name: _('Cultivo'), value: crops[cropMonitoring?.crop as keyof typeof crops]?.name },
            ...(shared ? [] : [{ name: _('Híbrido'), value: hybridNames.join(', ') || '-' }]),
            { name: _('Campaña'), value: `${plantingYear} ${harvestYear ? `-${harvestYear}` : ''}` },
            { name: _('Área del lote'), value: fieldArea || '-' },
            ...(shared
                ? []
                : [
                      {
                          name: _('Fecha de siembra'),
                          value: cropMonitoring?.harvestDate ? formatDate(new Date(cropMonitoring?.plantingDate)) : '-',
                      },
                  ]),
            {
                name: _('Fecha de cosecha'),
                value: cropMonitoring?.harvestDate ? formatDate(new Date(cropMonitoring?.harvestDate)) : '-',
            },
            // Planting data is the crop monitoring's (each hybrid has its own).
            ...(!shared && cropMonitoring?.density
                ? [
                      {
                          name: _('Densidad de siembra'),
                          value:
                              `${units.toDensity(cropMonitoring.density, cropMonitoring.crop, 2).value} ${units.getUnit('density', cropMonitoring.crop)}` ||
                              '-',
                      },
                  ]
                : []),
            ...(report?.totalHarvestedArea
                ? [
                      {
                          name: _('{{units}} cosechadas', { units: units.getUnit('area') }),
                          value: `${units.toArea(report.totalHarvestedArea, 2).value} ${units.getUnit('area')}` || '-',
                      },
                  ]
                : []),
            ...(!shared && cropMonitoring?.plantedArea
                ? [
                      {
                          name: _('{{units}} sembradas', { units: units.getUnit('area') }),
                          value: `${units.toArea(cropMonitoring.plantedArea, 2).value} ${units.getUnit('area')}` || '-',
                      },
                  ]
                : []),
        ];
    }, [crops, cropMonitoring, report, units, fieldArea, plantingYear, harvestYear]);

    return (
        <Stack
            direction={'row'}
            justifyContent='start'
            flexWrap='wrap'
            alignItems='center'
            rowGap={0}
            columnGap={2}
            sx={{ p: 2, border: (t: Theme) => `1px solid ${t.palette.divider}`, backgroundColor: '#f5f5f5' }}
        >
            {data.map((item, key) => (
                <Stack direction='column' key={`${item.name}-${key}`} sx={{ pr: 2 }} justifyContent='start'>
                    <Typography variant='body2' color='primary'>
                        {item.value}
                    </Typography>
                    <Typography variant='body2' color='primary.mui.inactive'>
                        {item.name}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
};

export default ReportHeaderData;
